import React, { useState, useEffect, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { fetchBatches, fetchBatchStudents } from '../../api/adminApi';
import DataList from './DataList';
import DataCard from './DataCard';
import Pagination from '../common/Pagination';

const ITEMS_PER_PAGE = 10;

const BatchesSection = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Track expanded batch and its students
  const [expandedId, setExpandedId] = useState(null);
  const [nestedData, setNestedData] = useState({});      // { [batchId]: { loading, students } }

  // Load batches list
  const loadBatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchBatches(currentPage, ITEMS_PER_PAGE, searchTerm);
      setBatches(data.batches || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Error loading batches:', err);
      setError('Failed to load batches. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  // Toggle expand / collapse; fetch students on first open
  const handleToggle = useCallback(async (batch) => {
    if (expandedId === batch.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(batch.id);

    if (nestedData[batch.id]) return; // already fetched

    setNestedData((prev) => ({
      ...prev,
      [batch.id]: { loading: true, students: [] },
    }));

    try {
      const data = await fetchBatchStudents(batch.id);
      setNestedData((prev) => ({
        ...prev,
        [batch.id]: { loading: false, students: data.students || [] },
      }));
    } catch (err) {
      console.error(`Error loading students for batch ${batch.id}:`, err);
      setNestedData((prev) => ({
        ...prev,
        [batch.id]: { loading: false, students: [] },
      }));
    }
  }, [expandedId, nestedData]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedId(null);
  };

  return (
    <div>
      {error && (
        <div className="data-error-banner">
          <i className="bi bi-exclamation-triangle-fill" />
          {error}
        </div>
      )}

      <DataList
        searchPlaceholder="Search batches…"
        searchValue={searchTerm}
        onSearch={handleSearch}
        loading={loading}
        empty={!loading && batches.length === 0}
        emptyMessage={searchTerm ? 'No batches match your search.' : 'No batches found.'}
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        }
      >
        {batches.map((batch) => {
          const nested = nestedData[batch.id];
          const isExpanded = expandedId === batch.id;

          return (
            <DataCard
              key={batch.id}
              title={batch.name}
              subtitle={batch.created ? `Created ${new Date(batch.created).toLocaleDateString()}` : undefined}
              badge={`${batch._count?.students ?? 0} Students`}
              accentColor="var(--accent-blue)"
              expanded={isExpanded}
              onToggle={() => handleToggle(batch)}
            >
              {nested?.loading ? (
                <div className="data-nested-skeleton">
                  <Skeleton count={3} height={36} borderRadius={6} />
                </div>
              ) : nested?.students?.length === 0 ? (
                <div className="data-nested-empty">No students in this batch.</div>
              ) : (
                <div className="data-nested-table-wrap">
                  <table className="data-nested-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(nested?.students || []).map((student) => (
                        <tr key={student.id}>
                          <td>
                            <span className="data-avatar">
                              {`${student.firstName?.[0] ?? ''}${student.lastName?.[0] ?? ''}`}
                            </span>
                            {student.firstName} {student.lastName}
                          </td>
                          <td>{student.email}</td>
                          <td>
                            <span
                              className="data-status-badge"
                              style={{
                                color: student.isActive ? 'var(--accent-green)' : 'var(--text-muted)',
                                backgroundColor: student.isActive
                                  ? 'rgba(132,204,22,0.12)'
                                  : 'rgba(100,116,139,0.12)',
                              }}
                            >
                              {student.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </DataCard>
          );
        })}
      </DataList>
    </div>
  );
};

export default BatchesSection;
